import { Card, Control as ControlType } from '@yleisradio/areena-types';
import { PlayButton } from 'components/Button';
import { Control as ControlComponent } from 'components/Controls/Control';
import { Placeholder } from 'components/Controls/Control/Placeholder';
import { useControl } from 'components/Controls/Control/useControl';
import { useStartoverDialog } from 'components/StartoverDialog/useStartoverDialog';
import React from 'react';
import { MediaType, Region } from 'services/areena-api/types';
import { getControlKey } from 'utils/control';
import {
  GeoRestrictionNotification,
  useGeoRestrictionState,
} from './GeoRestrictionNotification/GeoRestrictionNotification';
import styles from './HeaderPlayControls.module.scss';
import { useTrackCardClick } from 'components/SnowplowEngagementAnalytics/hooks';

type Props = {
  card: Card;
  playControl: ControlType;
  programMediaType: MediaType | undefined;
  programRegion: Region | undefined;
  queueControl: ControlType | undefined;
  viewUuid: string;
};

export const HeaderPlayControls: React.FC<Props> = ({
  card,
  playControl: playControlProp,
  programMediaType,
  programRegion,
  queueControl,
  viewUuid,
}) => {
  const { trackPlayButtonClick } = useTrackCardClick(viewUuid);

  const isProgramGeoBlocked =
    useGeoRestrictionState(programRegion) !== 'no-restriction';

  const {
    control: playControl,
    action,
    executingAction,
    loading,
  } = useControl({ control: playControlProp, cardPointer: card.pointer });

  const { playFromStart, openStartoverDialog, startoverDialog } =
    useStartoverDialog({ card, cardViewId: viewUuid, playControl });

  const onPlayClick = !executingAction
    ? () => {
        if (playFromStart) {
          openStartoverDialog();
          return;
        }

        action?.();
        trackPlayButtonClick?.();
      }
    : undefined;

  return (
    <div className={styles.root}>
      <div className={styles.buttons}>
        <div className={styles.playControl}>
          {playControl ? (
            <PlayButton
              control={playControl}
              disabled={playControl.disabled || isProgramGeoBlocked}
              onClick={onPlayClick}
              padding={undefined}
              size="sm"
              variant="primary"
              width="full"
              dataTestId="play-control"
            />
          ) : null}
          {loading ? <Placeholder size="sm" /> : null}
        </div>
        {queueControl ? (
          <div className={styles.queueControl}>
            <ControlComponent
              control={queueControl}
              controlKey={getControlKey(queueControl)}
              as="button"
              size="sm"
              variant="secondary"
              dataTestId="queue-control"
            />
          </div>
        ) : null}
      </div>
      {isProgramGeoBlocked && (
        <div className={styles.geoRestrictionNotification}>
          <GeoRestrictionNotification
            programMediaType={programMediaType}
            programRegion={programRegion}
          />
        </div>
      )}
      {startoverDialog}
    </div>
  );
};
